import {
  resolveComponentSizeTokens,
  type InputSizeKey,
  type ThemeColorMode,
  type ThemeDefinition,
} from '@vidahealth/vida-ui';
import { pxTokenToNumber, resolveTokenReference } from '@vidahealth/vida-ui/utils';
import type { TextStyle, ViewStyle } from 'react-native';

// vida-ui's `Textarea` ships fixed NativeWind classes (`h-[100px]`, `text-lg`,
// `border-primary-700` on focus) instead of reading `theme.tokens.components.input`
// the way `Input` does, so under `onboardingTheme` a comment box renders 18px
// text where every other field is 16px, and a `primary-700` focus ring where
// the theme says `primary.500` (see PR #73 review). vida-ui doesn't publish
// its own `resolveInputStyle`/`resolveInputFieldStyle`/`resolveInputBorderColor`
// — `components/input/utils.ts` isn't in its package `exports` map — so these
// mirror that logic with only the public building blocks
// (`resolveComponentSizeTokens`/`resolveTokenReference`/`pxTokenToNumber`),
// keyed off the same `tokens.components.input` `Input` uses, so a `TextArea`
// at a given `size` matches the `TextField` next to it in the same flow.

function resolveInputSizeTokens(theme: ThemeDefinition, size: InputSizeKey) {
  return resolveComponentSizeTokens(theme.tokens.components.input.sizes, size, ['lg', 'md']);
}

export function resolveTextAreaBorderColor(
  theme: ThemeDefinition,
  state: 'default' | 'focused' | 'invalid',
  colorMode: ThemeColorMode = 'light'
): string | undefined {
  const inputTokens = theme.tokens.components.input;

  if (state === 'invalid')
    return resolveTokenReference(inputTokens.invalidBorderColor, theme, colorMode);
  if (state === 'focused')
    return resolveTokenReference(inputTokens.focusBorderColor, theme, colorMode);

  return resolveTokenReference(inputTokens.borderColor, theme, colorMode);
}

export function resolveTextAreaContainerStyle(
  theme: ThemeDefinition,
  size: InputSizeKey
): ViewStyle {
  const inputTokens = theme.tokens.components.input;
  const sizeTokens = resolveInputSizeTokens(theme, size);
  const borderRadius = sizeTokens?.borderRadius ?? inputTokens.borderRadius;

  return {
    borderRadius: pxTokenToNumber(resolveTokenReference(borderRadius, theme)),
  };
}

export function resolveTextAreaFieldStyle(theme: ThemeDefinition, size: InputSizeKey): TextStyle {
  const sizeTokens = resolveInputSizeTokens(theme, size);

  return {
    fontSize: sizeTokens
      ? pxTokenToNumber(resolveTokenReference(sizeTokens.fontSize, theme))
      : undefined,
    paddingHorizontal: sizeTokens
      ? pxTokenToNumber(resolveTokenReference(sizeTokens.paddingHorizontal, theme))
      : undefined,
  };
}
